import { useEffect, useState } from 'react';

import { fetchBlobUrl } from '../api/client';

/**
 * A photograph from the evidence store.
 *
 * It cannot be an `<img src="/api/evidence/…">`, and that is the whole reason none of these
 * were visible. The panel authenticates with a bearer token held in `localStorage` and
 * attached by the fetch client; a browser loading an `<img>` sends no such header, so every
 * evidence request arrived unauthenticated, answered 401, and rendered as a broken icon. The
 * count beside it said "4 evidence file(s)", so the screen looked merely sparse rather than
 * broken.
 *
 * So the bytes are fetched the same way every other request is, and handed to the `<img>` as
 * an object URL. The URL is revoked on unmount — a gallery that opens and closes twenty
 * times otherwise holds twenty images in memory for the life of the page.
 *
 * The three states are all drawn. A photograph that failed to load must never look like one
 * that was never taken: this screen exists so somebody can decide whether a visit happened,
 * and an empty space is the one answer that would let them assume wrongly.
 */
export function EvidenceImage({
  evidenceId,
  alt,
  height = 150,
}: {
  evidenceId: string;
  alt: string;
  height?: number;
}): JSX.Element {
  const [state, setState] = useState<{ url: string } | { error: string } | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let live = true;

    void fetchBlobUrl(`/evidence/${evidenceId}/file`)
      .then((created) => {
        url = created;
        // Resolved after the component went away: revoke rather than set state on nothing.
        if (!live) {
          URL.revokeObjectURL(created);
          return;
        }
        setState({ url: created });
      })
      .catch((cause: unknown) => {
        if (live) setState({ error: cause instanceof Error ? cause.message : 'Could not load' });
      });

    return () => {
      live = false;
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [evidenceId]);

  if (state === null) {
    return <div className="evidence-tile evidence-tile-empty" style={{ height }} />;
  }

  if ('error' in state) {
    return (
      <div className="evidence-tile evidence-tile-failed" style={{ height }}>
        Could not load
      </div>
    );
  }

  return (
    <a href={state.url} target="_blank" rel="noreferrer" title="Open full size">
      <img className="evidence-tile" src={state.url} alt={alt} style={{ height }} />
    </a>
  );
}
